import { Close as CloseIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import CenteredCircularProgress from '../../designSystem/CircularProgress';
export { useDocumentPreview } from './useDocumentPreview';

interface ContentRendererProps {
  blobUrl: string;
  fileName: string;
  onError?: (error: string) => void;
}

const ImageRenderer: React.FC<ContentRendererProps> = ({
  blobUrl,
  fileName,
  onError,
}) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      p: 2,
    }}
  >
    <img
      src={blobUrl}
      alt={fileName}
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
      }}
      onError={() => onError?.('Error al cargar la imagen')}
    />
  </Box>
);

const PDFRenderer: React.FC<ContentRendererProps> = ({ blobUrl, fileName }) => (
  <iframe
    src={blobUrl}
    style={{
      width: '100%',
      height: '100%',
      border: 'none',
      minHeight: '600px',
    }}
    title={fileName}
  />
);

const TextRenderer: React.FC<ContentRendererProps> = ({
  blobUrl,
  fileName,
  onError,
}) => {
  const [textContent, setTextContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTextContent = async () => {
      try {
        const response = await fetch(blobUrl);
        const text = await response.text();
        setTextContent(text);
      } catch (error) {
        onError?.('Error al cargar el archivo de texto');
      } finally {
        setLoading(false);
      }
    };

    loadTextContent();
  }, [blobUrl, onError]);

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        overflow: 'auto',
      }}
    >
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          fontFamily: 'monospace',
          fontSize: '14px',
          margin: 0,
        }}
      >
        {textContent}
      </pre>
    </Box>
  );
};

const UnsupportedRenderer: React.FC<ContentRendererProps> = ({ fileName }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <Typography variant="h6" color="text.secondary">
      Tipo de archivo no soportado
    </Typography>
    <Typography variant="body2" color="text.secondary">
      No se puede previsualizar: {fileName}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Por favor, descarga el archivo para verlo.
    </Typography>
  </Box>
);

export const canPreviewFile = (
  contentType: string,
  fileName: string,
): boolean => {
  const lowerContentType = contentType.toLowerCase();
  const lowerFileName = fileName.toLowerCase();

  if (
    lowerFileName.match(
      /\.(txt|md|json|xml|html|css|js|ts|jsx|tsx|csv|log)$/,
    ) ??
    lowerContentType.startsWith('text/')
  ) {
    return true;
  }

  if (
    lowerContentType.startsWith('image/') ||
    lowerFileName.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/)
  ) {
    return true;
  }

  if (
    lowerContentType === 'application/pdf' ||
    lowerFileName.endsWith('.pdf')
  ) {
    return true;
  }

  return false;
};

const getContentRenderer = (contentType: string, fileName: string) => {
  const lowerContentType = contentType.toLowerCase();
  const lowerFileName = fileName.toLowerCase();

  if (
    lowerFileName.match(/\.(txt|md|json|xml|html|css|js|ts|jsx|tsx|csv|log)$/)
  ) {
    return TextRenderer;
  }

  if (
    lowerFileName.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/) ??
    lowerContentType.startsWith('image/')
  ) {
    return ImageRenderer;
  }

  if (
    lowerContentType === 'application/pdf' ||
    lowerFileName.endsWith('.pdf')
  ) {
    return PDFRenderer;
  }

  return UnsupportedRenderer;
};

interface DocumentsPreviewerProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  blobUrl: string | null;
  contentType: string | null;
  loading: boolean;
  error: string | null;
  onDownload?: () => void;
  onError?: (error: string) => void;
}

const DocumentsPreviewer: React.FC<DocumentsPreviewerProps> = ({
  open,
  onClose,
  fileName,
  blobUrl,
  contentType,
  loading,
  error,
  onDownload,
  onError,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Previsualización: {fileName}</Typography>

        {onDownload && (
          <IconButton
            aria-label="descargar"
            onClick={onDownload}
            sx={(theme: Theme) => ({
              position: 'absolute',
              right: 48,
              top: 8,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
              },
            })}
          >
            <GetAppIcon />
          </IconButton>
        )}

        <IconButton
          aria-label="cerrar"
          onClick={onClose}
          sx={(theme: Theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
            },
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CenteredCircularProgress type="dialog" />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Hubo un error al cargar el archivo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No se puede previsualizar: {fileName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Por favor, descarga el archivo para verlo.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: '100%' }}>
            {blobUrl && contentType ? (
              React.createElement(getContentRenderer(contentType, fileName), {
                blobUrl,
                fileName,
                onError,
              })
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No hay archivo para previsualizar.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentsPreviewer;
